import { AuthenticationStore, refreshAccessToken } from "@istari-frontend/auth";
import { createWorkerHasher, type HashFn, type TransferOptions, type WorkerLike } from "@istari/istari-client";

let hasher: HashFn | undefined;

export function getBrowserHasher(): HashFn {
  hasher ??= createWorkerHasher(spawnHashWorker, { terminateOnIdle: true });
  return hasher;
}

export const browserTransferOptions: Readonly<TransferOptions> = {
  concurrency: 4,
  partSize: 32 * 1024 * 1024,
  multipartThreshold: 32 * 1024 * 1024,
  hasher: (...args) => getBrowserHasher()(...args),
};

export async function currentRegistryToken(): Promise<string | undefined> {
  await refreshAccessToken();
  return AuthenticationStore().getAccessToken() || undefined;
}

function spawnHashWorker(): WorkerLike {
  const worker = new Worker(new URL("./hash.worker.ts", import.meta.url), { type: "module" });
  const events = new EventTarget();
  const queued: Array<() => void> = [];
  let ready = false;
  let terminated = false;
  const timer = setTimeout(() => fail("startup timed out after 15000 ms"), 15_000);

  const terminate = () => {
    if (terminated) return;
    terminated = true;
    clearTimeout(timer);
    queued.length = 0;
    worker.terminate();
  };
  const fail = (message: string) => {
    if (terminated) return;
    events.dispatchEvent(new ErrorEvent("error", { message }));
    terminate();
  };
  worker.addEventListener("message", (event: MessageEvent<unknown>) => {
    if (terminated) return;
    const message = event.data;
    if (message && typeof message === "object" && "type" in message) {
      if (message.type === "hash_worker_ready") {
        if (ready) return;
        ready = true;
        clearTimeout(timer);
        queued.splice(0).forEach((send) => send());
        return;
      }
      if (message.type === "hash_worker_startup_error") {
        fail("message" in message ? String(message.message) : "startup failed");
        return;
      }
    }
    events.dispatchEvent(new MessageEvent("message", { data: message }));
  });
  worker.addEventListener("error", (event) => fail(event.message || "worker script failed"));
  worker.addEventListener("messageerror", () => fail("worker message could not be deserialized"));

  return {
    addEventListener: events.addEventListener.bind(events),
    removeEventListener: events.removeEventListener.bind(events),
    terminate,
    postMessage(message: unknown, options?: Transferable[] | StructuredSerializeOptions) {
      if (terminated) return;
      const send = () => {
        if (Array.isArray(options)) worker.postMessage(message, options);
        else worker.postMessage(message, options);
      };
      if (ready) send();
      else queued.push(send);
    },
  };
}
